@layer refine.base {
  :host {
    --_bg: var(--card-bg, var(--color-canvas));
    --_color: var(--card-color, var(--color-contrast-900));
    --_border: var(--card-border, var(--border));
    --_border-color: var(--card-border-color, var(--color-divider));
    --_radius: var(--card-radius, var(--rounded-lg));
    --_padding: var(--card-padding, var(--size-4));
    --_shadow: var(--card-shadow, var(--shadow-sm));
    --_hover-shadow: var(--card-hover-shadow, var(--shadow-md));

    position: relative;
    display: flex;
    flex-direction: column;
  }

  .card {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
    color: var(--_color);
    background: var(--_bg);
    border-radius: var(--_radius);
    backdrop-filter: blur(var(--blur-md));
    transition:
      box-shadow var(--transition-normal),
      transform var(--transition-normal),
      background var(--transition-normal),
      border-color var(--transition-normal);
  }

  /* ── Card Structure ───────────────────────── */

  .card-media {
    flex-shrink: 0;
    overflow: hidden;
  }

  .card-media img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: cover;
  }

  .card-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
  }

  .card-header,
  .card-content,
  .card-footer {
    padding: var(--_padding);
  }

  .card-header {
    padding-bottom: calc(var(--_padding) / 2);
  }

  .card-footer {
    padding-top: calc(var(--_padding) / 2);
  }

  .card-content {
    flex: 1;
  }

  .card-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--size-2);
    align-items: center;
    padding: var(--_padding);
    padding-top: calc(var(--_padding) / 2);
  }

  [hidden] {
    display: none;
  }
}

@layer refine.variants {
  /* Default — bordered card with shadow */
  :host(:not([variant], [color])) {
    --_shadow: var(--card-shadow, var(--shadow-sm));
  }

  :host(:not([variant], [color])) .card {
    border: var(--_border) solid var(--_border-color);
    box-shadow: var(--_shadow);
  }

  /* Default + color — tinted backdrop */
  :host(:not([variant])[color]) {
    --_bg: var(--card-bg, var(--_theme-backdrop));
    --_border-color: var(--card-border-color, var(--_theme-border));
  }

  :host(:not([variant])[color]) .card {
    border: var(--_border) solid var(--_border-color);
  }

  :host(:not([variant])[color]:hover) {
    --_bg: var(--card-bg, color-mix(in oklch, var(--_theme-base) 16%, var(--color-contrast-50)));
    --_border-color: var(--card-border-color, var(--_theme-focus));
  }

  /* Solid — fully filled with theme color */
  :host([variant='solid']) {
    --_bg: var(--card-bg, var(--_theme-base));
    --_color: var(--card-color, var(--_theme-contrast));
    --_border-color: var(--card-border-color, var(--_theme-base));
    --_shadow: var(--card-shadow, var(--shadow-sm));
  }

  :host([variant='solid']) .card {
    border: var(--_border) solid var(--_border-color);
    box-shadow: var(--_shadow);
  }

  /* Flat — tinted backdrop, subtle border */
  :host([variant='flat']) {
    --_border-color: var(--card-border-color, color-mix(in oklch, var(--_theme-border) 50%, transparent));
  }

  :host([variant='flat']) .card {
    border: var(--_border) solid var(--_border-color);
  }

  /* Glass — frosted blur panel */
  :host([variant='glass']) .card {
    text-shadow: var(--text-shadow-xs);
    border: var(--_border) solid var(--_border-color);
    box-shadow: var(--card-shadow, var(--shadow-md)), var(--inset-shadow-xs);
    backdrop-filter: blur(var(--blur-lg)) saturate(180%) brightness(1.05);
  }

  :host([variant='glass']:hover) .card {
    box-shadow: var(--card-hover-shadow, var(--shadow-xl)), var(--inset-shadow-sm);
    backdrop-filter: blur(var(--blur-xl)) saturate(200%) brightness(1.1);
  }

  :host([variant='glass'][interactive]:hover) .card {
    box-shadow: var(--card-hover-shadow, var(--shadow-2xl)), var(--inset-shadow-sm);
  }
}

@layer refine.overrides {
  /* ── Loading bar ──────────────────────────── */

  /* Bar lives inside .card which already has overflow:hidden,
       so translateX can sweep freely without any overflow. */
  .loading-bar {
    position: absolute;
    inset-inline-start: 0;
    top: 0;
    display: none;
    width: 65%;
    height: 3px;
    background: linear-gradient(90deg, transparent 0%, var(--_theme-base, var(--color-primary)) 50%, transparent 100%);
  }

  :host([loading]) .loading-bar {
    display: block;
    animation: var(--_motion-animation, loading-bar 1.4s linear infinite);
  }

  /* In RTL the loading bar sweeps right-to-left */
  :host(:dir(rtl)[loading]) .loading-bar {
    animation-direction: reverse;
  }

  /* ── Disabled ─────────────────────────────── */

  :host([disabled]) {
    pointer-events: none;
    opacity: 0.6;
  }

  :host([disabled][interactive]) .card {
    cursor: not-allowed;
  }

  /* ── Interactive hover/active ─────────────── */

  :host([interactive]:not([disabled]):hover) .card {
    box-shadow: var(--_hover-shadow);
    transform: translateY(-2px) scale(1.01);
  }

  :host([interactive]) .card {
    cursor: pointer;
    will-change: transform;
  }

  /* `:active` bubbles to every ancestor of whatever element is actually being pressed, so
     without the `:not(:has(...))` guard, pressing a nested interactive descendant (a swatch
     button, a slotted `<ore-button>`, a link, etc. — kept in sync with card.ts's
     `INTERACTIVE_DESCENDANT_SELECTOR`) would *also* visually "press" the whole card even though
     `handleClick` already ignores the click for activation purposes. `:host(:has(...))`
     (`:has()` as `:host()`'s functional argument) is invalid — `:host()` only accepts a compound
     selector and doesn't accept `:has()` inside it, so the bare `:host` pseudo-class chained
     directly with the rest (no parentheses around it) is used instead — same reasoning as
     list-item.css's identical `:host:has(...)` rules. */
  :host[interactive]:not([disabled]):active:not(
    :has(
      button:active,
      a[href]:active,
      input:active,
      select:active,
      textarea:active,
      summary:active,
      [role='button']:active,
      [role='link']:active
    )
  ) .card {
    box-shadow: var(--_shadow);
    transform: translateY(0) scale(0.99);
    transition:
      box-shadow var(--transition-fast),
      transform 120ms var(--ease-spring);
  }

  :host([interactive]:focus-visible) .card {
    outline: var(--border-2) solid var(--_theme-focus, var(--color-primary));
    outline-offset: var(--border-2);
  }

  /* ── Horizontal orientation ───────────────── */

  :host([orientation='horizontal']) .card {
    flex-direction: row;
  }

  :host([orientation='horizontal']) .card-media {
    width: 30%;
    min-width: 192px;
    max-width: 320px;
  }

  :host([orientation='horizontal']) .card-media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  :host([orientation='horizontal']) .card-body {
    flex: 1;
  }
}

@keyframes loading-bar {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(200%);
  }
}
